import styles from './GeofenceSelectedTooltip.module.scss';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { Tooltip } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { ModelName } from '@casops/store/api';
import classNames from 'classnames';
import { useIsGeofenceManagement } from '@casops/permissions';
import { useAppDispatch } from '@casops/store/hooks';
import { openSidePanel, SidePanel } from '@casops/store/slices/globalSlice';
import { GhostIconButton } from '@dmc/ui';
import { GeofenceProperties } from '@casops/store/apis/geofencesApi';
import { useLocaleFormatter } from '@casops/shared/hooks/useLocaleFormatter';

type GeofenceSelectedTooltipProps = Pick<
  GeofenceProperties,
  'id' | 'name' | 'pdrpasset' | 'pdrptime' | 'ra'
>;

export function GeofenceSelectedTooltip({
  id,
  name,
  pdrpasset,
  pdrptime,
  ra: requireAttention,
}: GeofenceSelectedTooltipProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showTimeAndDateFromIso } = useLocaleFormatter();

  const canEdit = useIsGeofenceManagement();

  const onView = useCallback(() => {
    dispatch(
      openSidePanel({
        sidePanel: SidePanel.ReadGeofence,
        modelId: id.toString(),
        editMode: true, // The side panel contains a readonly form, but it must use editMode=true to know to load the model data.
      }),
    );
  }, [dispatch, id]);

  const onEdit = useCallback(() => {
    navigate(`/home/geofence/edit/${id}`);
  }, [navigate, id]);

  return (
    <Tooltip
      className={classNames(styles.container, styles.selected)}
      direction="top"
      sticky={false}
      permanent={true}
      opacity={1}
      interactive={true}
      offset={[0, 44]}
    >
      <div className={styles.content}>
        <div className={styles.title}>{name}</div>
        {pdrpasset && <div className={styles.text}>{t('AddedVia', { name: pdrpasset })}</div>}
        {pdrptime && <div className={styles.text}>{showTimeAndDateFromIso(pdrptime)}</div>}
      </div>

      <div className={styles.divider} />

      <div className={styles.actions}>
        {canEdit ? (
          requireAttention ? (
            <GhostIconButton icon="open-icon" onClick={onEdit}>
              {t('Configure')}
            </GhostIconButton>
          ) : (
            <GhostIconButton icon="edit" onClick={onEdit}>
              {t('Model.Edit.Title', { modelName: ModelName.Geofence })}
            </GhostIconButton>
          )
        ) : (
          <GhostIconButton icon="open-icon" onClick={onView}>
            {t('ViewDetails')}
          </GhostIconButton>
        )}
      </div>
    </Tooltip>
  );
}

export default GeofenceSelectedTooltip;
